import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface EscalateReviewDialogProps {
  open: boolean;
  busy: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (note: string) => Promise<void>;
}

export function EscalateReviewDialog({
  open,
  busy,
  error,
  onCancel,
  onConfirm,
}: EscalateReviewDialogProps) {
  const [note, setNote] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setNote('');
    const timer = setTimeout(() => textareaRef.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open || busy) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel, open]);

  if (!open) return null;
  const trimmedNote = note.trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="escalate-review-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="escalate-review-title" className="text-base font-semibold text-gray-900">
              Escalate this review?
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              The campaign admin will receive your note by email and this task will move to their review queue.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label htmlFor="escalation-note" className="mt-4 block text-sm font-medium text-gray-800">
          Note to admin
        </label>
        <textarea
          ref={textareaRef}
          id="escalation-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={6}
          maxLength={20 * 1024}
          disabled={busy}
          required
          placeholder="Explain why this review needs admin attention."
          className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50"
        />
        {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm(trimmedNote)}
            disabled={busy || !trimmedNote}
            className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Escalating…' : 'Escalate review'}
          </button>
        </div>
      </div>
    </div>
  );
}
